/**
 * Converts URLs and @ mentions in text to clickable links
 * @param {string} text - The text that may contain URLs and mentions
 * @param {Array} lists - Array of lists for resolving mentions
 * @param {Function} onMentionClick - Callback when a mention is clicked (listId) => void
 * @returns {Array} Array of React elements (text nodes and <a> tags)
 */
export function linkifyText(text, lists = [], onMentionClick = null) {
  if (!text) return text;

  const parts = [];
  let lastIndex = 0;
  
  // First, find all mentions by trying to match against list names
  // Then find URLs
  const mentions = [];
  const urls = [];
  
  // Find mentions: look for @ and try to match against all list names
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '@') {
      // Try to match against all list names
      let bestMatch = null;
      let bestMatchLength = 0;
      
      for (const list of lists) {
        const listName = list.name;
        const mentionText = `@${listName}`;
        
        // Check if the mention text matches starting at position i
        if (text.substring(i, i + mentionText.length) === mentionText) {
          // Check if it's followed by a space, newline, end of string, or another @
          const nextChar = text[i + mentionText.length];
          if (!nextChar || nextChar === ' ' || nextChar === '\n' || nextChar === '@') {
            if (mentionText.length > bestMatchLength) {
              bestMatch = {
                index: i,
                text: mentionText,
                listName: listName,
                listId: list.id
              };
              bestMatchLength = mentionText.length;
            }
          }
        }
      }
      
      if (bestMatch) {
        mentions.push(bestMatch);
      }
    }
  }
  
  // Find URLs: http://, https://, www., or domain patterns
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}[^\s]*)/g;
  let urlMatch;
  while ((urlMatch = urlRegex.exec(text)) !== null) {
    const matchIndex = urlMatch.index;
    const matchText = urlMatch[0];
    // Check if this URL overlaps with any mention
    const overlaps = mentions.some(m => {
      const mEnd = m.index + m.text.length;
      return (matchIndex >= m.index && matchIndex < mEnd) ||
             (m.index >= matchIndex && m.index < matchIndex + matchText.length);
    });

    if (!overlaps) {
      urls.push({
        index: matchIndex,
        text: matchText
      });
    }
  }
  
  // Combine and sort all matches by index
  const allMatches = [
    ...mentions.map(m => ({ ...m, type: 'mention' })),
    ...urls.map(u => ({ ...u, type: 'url' }))
  ].sort((a, b) => a.index - b.index);
  
  // Remove overlapping matches (mentions take priority)
  const filteredMatches = [];
  for (const match of allMatches) {
    const matchLength = match.type === 'mention' ? match.text.length : match.text.length;
    const overlaps = filteredMatches.some(fm => {
      const fmLength = fm.type === 'mention' ? fm.text.length : fm.text.length;
      const fmEnd = fm.index + fmLength;
      const mEnd = match.index + matchLength;
      return (match.index >= fm.index && match.index < fmEnd) ||
             (fm.index >= match.index && fm.index < mEnd);
    });
    
    if (!overlaps) {
      filteredMatches.push(match);
    }
  }
  
  // Process matches in order
  for (const match of filteredMatches) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    if (match.type === 'mention') {
      // Handle mention
      if (onMentionClick) {
        parts.push(
          <button
            key={`mention-${match.index}`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMentionClick(match.listId);
            }}
            style={{
              color: 'var(--primary-color)',
              textDecoration: 'underline',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit'
            }}
            title={`Go to ${match.listName}`}
          >
            {match.listName}
          </button>
        );
      } else {
        // No click handler, render as plain text (without @)
        parts.push(match.listName);
      }
    } else {
      // Handle URL
      let url = match.text;
      // Add http:// if it starts with www.
      if (url.startsWith('www.')) {
        url = 'https://' + url;
      }
      
      parts.push(
        <a
          key={`url-${match.index}`}
          href={url}
          target="new"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ color: 'var(--primary-color)', textDecoration: 'underline' }}
        >
          {match.text}
        </a>
      );
    }

    const matchLength = match.text.length;
    lastIndex = match.index + matchLength;
  }

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // If no matches were found, return the original text
  return parts.length > 0 ? parts : text;
}

